// Flyer QR hooks — ONE config for every printed flyer.
//
// Each flyer's QR encodes https://monarchdetailing.art/flyer?h=<keyword>.
// /flyer reads `h`, shows that hook's headline (message match with the flyer
// that was scanned), logs the scan under that keyword, and carries it into the
// booking so bookings attribute to hooks.
//
// A new hook = add one line here and print a QR with the new `h`. No new page.
// Missing / unknown / retired keywords show DEFAULT_HOOK's headline and are
// logged as 'unknown' (the raw value is kept in flyer_events.hook_raw).
//
// ⚠️ DRAFT HEADLINES: these are placeholders written without the flyer art.
// Replace each `headline` with the EXACT text printed on that flyer before the
// flyers go out — the page must read like the flyer's continuation.
// Retired (never add back): goldfish, doghair, reset, fresh, inside, detail,
// and the harsh-guilt set.

export const DEFAULT_HOOK = 'newcar';

export const FLYER_HOOKS = {
  // ── Dream ──
  newcar:   { group: 'dream', headline: 'Make it feel like a new car again.' },
  dayone:   { group: 'dream', headline: 'Remember day one? Let’s get it back.' },
  again:    { group: 'dream', headline: 'Love your car again.' },
  breathe:  { group: 'dream', headline: 'Breathe easy in your own car.' },
  bought:   { group: 'dream', headline: 'Feel like you just bought it.' },
  smell:    { group: 'dream', headline: 'Bring back that fresh-car smell.' },
  showroom: { group: 'dream', headline: 'Showroom-clean inside — right in your driveway.' },
  monday:   { group: 'dream', headline: 'Start Monday in a spotless car.' },
  luxury:   { group: 'dream', headline: 'Everyday car. Luxury-clean inside.' },
  crown:    { group: 'dream', headline: 'Give your interior the royal treatment.' },

  // ── Fused ──
  back:     { group: 'fused', headline: 'Get your back seat back.' },
  flip:     { group: 'fused', headline: 'Flip your interior from messy to fresh.' },
  love:     { group: 'fused', headline: 'Fall back in love with your car.' },
  two:      { group: 'fused', headline: 'Two minutes to book. A whole new interior.' },
  meeting:  { group: 'fused', headline: 'Pull up to your next meeting spotless.' },
  quiet:    { group: 'fused', headline: 'A clean car is a quiet mind.' },
  date:     { group: 'fused', headline: 'Date-night ready, inside.' },
  friday:   { group: 'fused', headline: 'Spotless by Friday.' },
  paid:     { group: 'fused', headline: 'You paid for that car. Enjoy it clean.' },
  trip:     { group: 'fused', headline: 'Road-trip ready on the inside.' },
  mom:      { group: 'fused', headline: 'Mom’s car, finally spotless.' },
  vibe:     { group: 'fused', headline: 'Same car. Better vibe inside.' },
  leather:  { group: 'fused', headline: 'Bring your seats back to life.' },
  night:    { group: 'fused', headline: 'Drive home every night to a clean car.' },
  seat:     { group: 'fused', headline: 'Crumbs in the car seat? We’ve got it.' },
  live:     { group: 'fused', headline: 'You practically live in your car. Make it nice.' },
  three:    { group: 'fused', headline: 'Every row. Every seat. Spotless.' },
  dealer:   { group: 'fused', headline: 'Dealer-fresh without the dealer.' },
  shotgun:  { group: 'fused', headline: 'Make riding shotgun a treat.' },
  grown:    { group: 'fused', headline: 'A grown-up car, cleaned like one.' },
  miles:    { group: 'fused', headline: 'High miles? Make it feel fresh.' },
  holiday:  { group: 'fused', headline: 'Holiday-ready before the family piles in.' },

  // ── Guilt-lite ──
  tell:     { group: 'guilt-lite', headline: 'We won’t tell anyone how messy it got.' },
  mess:     { group: 'guilt-lite', headline: 'No judgment. Just a clean car.' },
  five:     { group: 'guilt-lite', headline: 'Five minutes to book. Zero judgment.' },
  home:     { group: 'guilt-lite', headline: 'Your car is your second home. Treat it like one.' },
  sheets:   { group: 'guilt-lite', headline: 'You change your sheets. Your seats need it too.' },
  mean:     { group: 'guilt-lite', headline: 'You’ve been meaning to clean it. Let us.' },
  undo:     { group: 'guilt-lite', headline: 'Undo months of messes in one visit.' },
  repay:    { group: 'guilt-lite', headline: 'Your car’s done a lot for you. Return the favor.' },
  blame:    { group: 'guilt-lite', headline: 'Blame the kids. We’ll clean it up.' },
};

// Resolve a raw ?h= value to a configured keyword, or 'unknown'.
export const resolveHook = (raw) => {
  const h = String(raw || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(FLYER_HOOKS, h) ? h : 'unknown';
};

// Headline for a resolved hook ('unknown' → default hook's headline).
export const hookHeadline = (hook) => (FLYER_HOOKS[hook] || FLYER_HOOKS[DEFAULT_HOOK]).headline;
